import { CollectionID, Rarity, ItemCategory } from '@stalker/core';
import { SoundPreset } from '#shared/lib/sound-engine.js';

export interface LootConfig {
  collection: CollectionID;
  min: number;
  max: number;
}

export interface RoomConfig {
  id: string;
  name: string;
  description?: string;
  loot?: LootConfig[];
  soundPreset?: SoundPreset;
}

export interface BuildingConfig {
  id: string;
  name: string;
  type: 'residential' | 'school' | 'shop' | 'hospital' | 'garage' | 'military' | 'factory' | 'church' | 'hotel' | 'library' | 'bunker';
  description: string;
  dangerLevel: number;
  imageUrl?: string;
  rooms: RoomConfig[];
  soundPreset?: SoundPreset;
}

export interface StreetConfig {
  id: string;
  name: string;
  description: string;
  dangerLevel: number;
  imageUrl?: string;
  buildings: BuildingConfig[];
  soundPreset?: SoundPreset;
}

export interface CityConfig {
  id: string;
  name: string;
  description: string;
  streets: StreetConfig[];
}

export const WORLD_CONFIG: CityConfig = {
  id: 'city_pripyat',
  name: 'Припять',
  description: 'Мёртвый город, застывший во времени. Бывший центр атомограда.',
  streets: [
    {
      id: 'street_lenina',
      name: 'Улица Ленина',
      description: 'Главная транспортная артерия города. Вдоль обочин ржавеют брошенные автомобили, а тротуары поросли густым кустарником.',
      dangerLevel: 1,
      imageUrl: '/assets/streets/lenina.avif',
      soundPreset: SoundPreset.WIND,
      buildings: [
        {
          id: 'res1',
          name: 'Пятиэтажка №12',
          type: 'residential',
          description: 'Серая хрущёвка с выбитыми окнами.',
          dangerLevel: 1,
          imageUrl: '/assets/streets/lenina_12.avif',
          soundPreset: SoundPreset.HOUSE,
          rooms: [
            { id: 'kitchen', name: 'Кухня', soundPreset: SoundPreset.DRIP, loot: [{ collection: 'construction', min: 2, max: 3 }, { collection: 'container', min: 1, max: 2 }] },
            { id: 'corridor', name: 'Коридор', soundPreset: SoundPreset.CREAK },
            { id: 'bedroom', name: 'Спальня' },
            { id: 'bathroom', name: 'Ванная', soundPreset: SoundPreset.ECHO_DRIP }
          ]
        },
        {
          id: 'res2',
          name: 'Коттедж Иванова',
          type: 'residential',
          description: 'Когда-то уютный дом. Обугленный остов.',
          dangerLevel: 2,
          soundPreset: SoundPreset.HOUSE,
          imageUrl: '/assets/streets/lenina_11.avif',
          rooms: [
            { id: 'kitchen', name: 'Кухня', soundPreset: SoundPreset.DRIP, loot: [{ collection: 'construction', min: 1, max: 2 }] },
            { id: 'bedroom', name: 'Спальня', loot: [{ collection: 'container', min: 1, max: 2 }] }
          ]
        },
        {
          id: 'shop1',
          name: 'Магазин «Продукты»',
          type: 'shop',
          description: 'Бывший продовольственный. Полки пусты.',
          dangerLevel: 1,
          imageUrl: '/assets/streets/lenina_shop.avif',
          soundPreset: SoundPreset.HOUSE,
          rooms: [
            { id: 'sales_floor', name: 'Торговый зал', loot: [{ collection: 'storage', min: 2, max: 3 }] },
            { id: 'storage', name: 'Склад' }
          ]
        },
        {
          id: 'church1',
          name: 'Церковь Покрова',
          type: 'church',
          description: 'Древний храм. Странная энергия внутри.',
          imageUrl: '/assets/streets/lenina_church.avif',
          dangerLevel: 3,
          soundPreset: SoundPreset.RESONANCE,
          rooms: [
            { id: 'nave', name: 'Главный зал', loot: [{ collection: 'storage', min: 1, max: 2 }] },
            { id: 'crypt', name: 'Крипта' }
          ]
        }
      ]
    },
    {
      id: 'street_hospital',
      name: 'Больничный переулок',
      description: 'Короткая улица, ведущая к медицинскому городку.',
      dangerLevel: 1,
      soundPreset: SoundPreset.WIND,
      imageUrl: '/assets/streets/hospital_ln.avif',
      buildings: [
        {
          id: 'hosp1',
          name: 'Городская больница',
          type: 'hospital',
          description: 'Запах лекарств и гнили.',
          dangerLevel: 2,
          soundPreset: SoundPreset.MED_HUM,
          imageUrl: '/assets/buildings/hospital.avif',
          rooms: [
            { id: 'ward', name: 'Палата', loot: [{ collection: 'container', min: 1, max: 2 }] },
            { id: 'surgery', name: 'Операционная' },
            { id: 'pharmacy', name: 'Аптека' }
          ]
        },
        {
          id: 'pharmacy1',
          name: 'Аптека №5',
          type: 'shop',
          description: 'Аптека с уцелевшими полками.',
          dangerLevel: 2,
          imageUrl: '/assets/buildings/pharmacy.avif',
          soundPreset: SoundPreset.MED_HUM,
          rooms: [
            { id: 'sales_floor', name: 'Торговый зал', loot: [{ collection: 'storage', min: 1, max: 2 }] }
          ]
        }
      ]
    },
    {
      id: 'street_school',
      name: 'Школьный квартал',
      description: 'Район образовательных учреждений.',
      dangerLevel: 2,
      imageUrl: '/assets/streets/school_region.avif',
      soundPreset: SoundPreset.WIND,
      buildings: [
        {
          id: 'school1',
          name: 'Школа №12',
          type: 'school',
          description: 'Трёхэтажная школа.',
          dangerLevel: 2,
          imageUrl: '/assets/buildings/school_12.avif',
          soundPreset: SoundPreset.HOUSE,
          rooms: [
            { id: 'classroom', name: 'Класс физики', loot: [{ collection: 'stationery', min: 2, max: 3 }] },
            { id: 'gym', name: 'Спортзал' }
          ]
        },
        {
          id: 'lib1',
          name: 'Библиотека им. Пушкина',
          type: 'library',
          description: 'Книги хранят знания довоенного мира.',
          dangerLevel: 1,
          imageUrl: '/assets/buildings/library.avif',
          soundPreset: SoundPreset.RESONANCE,
          rooms: [
            { id: 'reading_hall', name: 'Читальный зал', loot: [{ collection: 'stationery', min: 1, max: 2 }] },
            { id: 'archive', name: 'Архив', loot: [{ collection: 'storage', min: 1, max: 2 }] }
          ]
        }
      ]
    },
    {
      id: 'street_factory',
      name: 'Промышленный проезд',
      description: 'Индустриальная зона на окраине города.',
      dangerLevel: 3,
      soundPreset: SoundPreset.WIND,
      buildings: [
        {
          id: 'factory1',
          name: 'Завод «Прогресс»',
          type: 'factory',
          description: 'Огромный заводской комплекс.',
          dangerLevel: 3,
          soundPreset: SoundPreset.METAL,
          rooms: [
            { id: 'workshop', name: 'Цех', loot: [{ collection: 'technical', min: 2, max: 3 }] },
            { id: 'control_room', name: 'Пультовая', soundPreset: SoundPreset.ELECTRIC }
          ]
        },
        {
          id: 'mil1',
          name: 'Военная часть №3141',
          type: 'military',
          description: 'Заброшенная воинская часть.',
          dangerLevel: 4,
          soundPreset: SoundPreset.ELECTRIC,
          rooms: [
            { id: 'barracks', name: 'Казарма', loot: [{ collection: 'technical', min: 2, max: 3 }] },
            { id: 'armory', name: 'Оружейная', loot: [{ collection: 'elite', min: 0, max: 1 }] }
          ]
        }
      ]
    },
    {
      id: 'street_bunker',
      name: 'Бункерный тупик',
      description: 'Тупиковая ветка, ведущая к секретному объекту.',
      dangerLevel: 4,
      soundPreset: SoundPreset.WIND,
      buildings: [
        {
          id: 'bunker1',
          name: 'Бункер «Сигнал»',
          type: 'bunker',
          description: 'Секретный бункер связи.',
          dangerLevel: 4,
          soundPreset: SoundPreset.VENTILATION,
          rooms: [
            { id: 'control_b', name: 'Центр управления', soundPreset: SoundPreset.ELECTRIC, loot: [{ collection: 'elite', min: 2, max: 3 }] },
            { id: 'lab_b', name: 'Секретная лаб.' }
          ]
        }
      ]
    }
  ]
};
